package com.schoolconnect.app.feature.schools.edit

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import com.schoolconnect.app.designsystem.AppDropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppLabeledField
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppInputBorder
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSecondaryBackground
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppSoftSurface
import com.schoolconnect.app.designsystem.AppSurface
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.arrow_left
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.map_pin
import ischoolconnect.sharedui.generated.resources.phone_call
import org.jetbrains.compose.resources.painterResource

internal val EditSchoolBackground = AppBackground
internal val EditSchoolCard = AppSurface
internal val EditSchoolPrimary = AppPrimary
internal val EditSchoolPrimaryHover = Color(0xFF084BE0)
internal val EditSchoolNavy = AppNavy
internal val EditSchoolMuted = AppMuted
internal val EditSchoolBorder = AppBorder
internal val EditSchoolInputBorder = AppInputBorder
internal val EditSchoolReadOnlyBackground = AppSecondaryBackground
internal val EditSchoolDanger = AppDanger
internal val EditSchoolIconTileBackground = AppPrimarySoft

internal val EditSchoolHeaderShape = RoundedCornerShape(8.dp)
internal val EditSchoolFieldShape = RoundedCornerShape(5.dp)

internal data class EditSchoolOption(
    val code: String,
    val label: String
)

internal val editSchoolStatusOptions = listOf(
    EditSchoolOption(SchoolKeys.V_STATUS_ACTIVE, "Active"),
    EditSchoolOption(SchoolKeys.V_STATUS_INACTIVE, "Inactive")
)

internal fun editSchoolBoardOptions(): List<EditSchoolOption> =
    SystemLookupOptions.activeItems("SCHOOL_BOARD").mapNotNull { item ->
        val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
        EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
    }

internal fun editSchoolTypeOptions(): List<EditSchoolOption> =
    SystemLookupOptions.activeItems("SCHOOL_TYPE").mapNotNull { item ->
        val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
        EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
    }
internal fun editSchoolMediumOptions(): List<EditSchoolOption> =
    SystemLookupOptions.activeItems("SCHOOL_MEDIUM").mapNotNull { item ->
        val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
        EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
    }

internal fun editSchoolStatusLabel(code: String): String =
    editSchoolStatusOptions.firstOrNull { it.code == code }?.label ?: code

internal fun editSchoolBoardLabel(code: String): String =
    SystemLookupOptions.label("SCHOOL_BOARD", code)

internal fun editSchoolTypeLabel(code: String): String =
    SystemLookupOptions.label("SCHOOL_TYPE", code)

internal fun editSchoolMediumLabel(code: String): String =
    SystemLookupOptions.label("SCHOOL_MEDIUM", code)

internal fun formatEditSchoolDate(value: String): String {
    val normalized = value.trim()
    if (normalized.isBlank()) return ""

    val datePart = normalized.substringBefore("T").takeIf { it.length >= 10 }?.take(10)
        ?: return normalized
    val parts = datePart.split("-")
    if (parts.size != 3) return normalized

    val year: String
    val month: Int
    val day: Int
    when {
        parts[0].length == 4 -> {
            year = parts[0]
            month = parts[1].toIntOrNull() ?: return normalized
            day = parts[2].toIntOrNull() ?: return normalized
        }
        parts[2].length == 4 -> {
            year = parts[2]
            month = parts[1].toIntOrNull() ?: return normalized
            day = parts[0].toIntOrNull() ?: return normalized
        }
        else -> return normalized
    }
    if (month !in 1..12 || day !in 1..31) return normalized

    return "${day.toString().padStart(2, '0')} ${shortEditSchoolMonthLabel(month)} $year"
}

private fun shortEditSchoolMonthLabel(month: Int): String = when (month) {
    1 -> "Jan"
    2 -> "Feb"
    3 -> "Mar"
    4 -> "Apr"
    5 -> "May"
    6 -> "Jun"
    7 -> "Jul"
    8 -> "Aug"
    9 -> "Sep"
    10 -> "Oct"
    11 -> "Nov"
    12 -> "Dec"
    else -> ""
}

internal fun defaultEditSchoolFormValues(): EditSchoolFormValues = EditSchoolFormValues(
    id = "school_aurora_001",
    status = SchoolKeys.V_STATUS_ACTIVE,
    schoolName = "Aurora Public School",
    schoolCode = "AUR00001",
    board = SchoolKeys.V_BOARD_CBSE,
    type = "GOVERNMENT",
    medium = "ENGLISH",
    createdOn = "12 Jun 2026",
    createdBy = "School Administrator",
    updatedOn = "18 Jun 2026",
    updatedBy = "Super Admin",
    addressLine1 = "12 Main Road, Sector 5",
    addressLine2 = "Near Bus Stand",
    city = "Delhi",
    district = "South Delhi",
    state = "Delhi",
    country = "India",
    pinCode = "110021",
    schoolPhone = "9876543210",
    schoolEmail = "info@aurora.edu",
    primaryContact = "Meera Iyer",
    primaryPhone = "9876543211",
    primaryEmail = "meera.iyer@aurora.edu",
    secondaryContact = "Rahul Iyer",
    secondaryPhone = "9876543212",
    secondaryEmail = "rahul.iyer@aurora.edu",
    schoolDescription = "A nurturing CBSE school focused on academics, sports, and holistic development."
)

internal fun schoolRowToEditSchoolFormValues(
    row: com.schoolconnect.app.feature.schools.SchoolRowUi?
): EditSchoolFormValues {
    val school = row ?: return defaultEditSchoolFormValues()
    return EditSchoolFormValues(
        id = school.id,
        status = if (school.isActive) SchoolKeys.V_STATUS_ACTIVE else SchoolKeys.V_STATUS_INACTIVE,
        schoolName = school.name,
        schoolCode = school.code,
        board = school.board,
        type = school.schoolType,
        medium = school.medium,
        createdOn = formatEditSchoolDate(school.createdOn),
        createdBy = school.createdBy,
        updatedOn = formatEditSchoolDate(school.updatedOn),
        updatedBy = school.updatedBy,
        addressLine1 = school.addressLine1,
        addressLine2 = school.addressLine2,
        city = school.city,
        district = school.district,
        state = school.state,
        country = school.country,
        pinCode = school.pinCode,
        schoolPhone = school.schoolPhone,
        schoolEmail = school.schoolEmail,
        primaryContact = school.contactName,
        primaryPhone = school.primaryPhone,
        primaryEmail = school.primaryEmail,
        secondaryContact = school.secondaryContactName,
        secondaryPhone = school.secondaryPhone,
        secondaryEmail = school.secondaryEmail,
        attachmentFileId = school.attachmentFileId,
        schoolLogoUrl = school.schoolLogoUrl,
        schoolDescription = school.schoolDescription
    )
}

@Composable
internal fun EditSchoolTextArea(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    required: Boolean = false,
    placeholder: String = "",
    isError: Boolean = false,
    errorText: String = ""
) {
    AppLabeledField(
        label = label,
        required = required,
        errorText = errorText,
        modifier = modifier,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        labelColor = EditSchoolNavy,
        requiredColor = EditSchoolDanger,
        labelFontWeight = FontWeight.SemiBold,
        errorStyle = MaterialTheme.typography.bodySmall,
        errorColor = EditSchoolDanger,
        errorFontWeight = FontWeight.Normal
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(78.dp),
            shape = EditSchoolFieldShape,
            color = EditSchoolCard,
            border = BorderStroke(1.dp, if (isError) EditSchoolDanger else EditSchoolInputBorder)
        ) {
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                enabled = true,
                readOnly = false,
                singleLine = false,
                maxLines = 3,
                textStyle = MaterialTheme.typography.bodyMedium.copy(
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Normal,
                    color = EditSchoolNavy
                ),
                cursorBrush = SolidColor(EditSchoolPrimary),
                keyboardOptions = KeyboardOptions.Default,
                decorationBox = { innerTextField ->
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.TopStart
                    ) {
                        if (value.isBlank() && placeholder.isNotBlank()) {
                            Text(
                                text = placeholder,
                                style = MaterialTheme.typography.bodyMedium,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Normal,
                                color = EditSchoolMuted,
                                maxLines = 1
                            )
                        }
                        innerTextField()
                    }
                }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun EditSchoolHeader(onBack: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            modifier = Modifier.size(48.dp),
            shape = EditSchoolHeaderShape,
            color = EditSchoolCard,
            border = BorderStroke(1.dp, EditSchoolBorder),
            onClick = onBack
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    painter = painterResource(Res.drawable.arrow_left),
                    contentDescription = "Back",
                    tint = EditSchoolNavy,
                    modifier = Modifier.size(22.dp)
                )
            }
        }
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = "Edit School",
                style = MaterialTheme.typography.headlineSmall,
                fontSize = 26.sp,
                fontWeight = FontWeight.ExtraBold,
                color = EditSchoolNavy
            )
            Text(
                text = "Update school details and contact information",
                style = MaterialTheme.typography.bodyMedium,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
                color = EditSchoolMuted
            )
        }
    }
}

@Composable
internal fun EditSchoolSectionCard(
    compact: Boolean,
    @Suppress("UNUSED_PARAMETER") iconPainter: Painter,
    content: @Composable () -> Unit
) {
    val sectionBorder = Color(0xFFE2E8F0)
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
        colors = CardDefaults.cardColors(containerColor = EditSchoolCard),
        border = BorderStroke(1.dp, sectionBorder),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = if (compact) 14.dp else 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content()
        }
    }
}

@Composable
internal fun EditSchoolFieldLabel(
    label: String,
    required: Boolean = false
) {
    Text(
        text = buildAnnotatedString {
            append(label)
            if (required) {
                append(" ")
                withStyle(SpanStyle(color = EditSchoolDanger)) {
                    append("*")
                }
            }
        },
        style = MaterialTheme.typography.bodyMedium,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        color = EditSchoolNavy,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis
    )
}

@Composable
internal fun EditSchoolTextField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    required: Boolean = false,
    placeholder: String = "",
    readOnly: Boolean = false,
    keyboardType: KeyboardType = KeyboardType.Text,
    isError: Boolean = false,
    errorText: String = ""
) {
    AppLabeledField(
        label = label,
        required = required,
        errorText = errorText,
        modifier = modifier,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        labelColor = EditSchoolNavy,
        requiredColor = EditSchoolDanger,
        labelFontWeight = FontWeight.SemiBold,
        errorStyle = MaterialTheme.typography.bodySmall,
        errorColor = EditSchoolDanger,
        errorFontWeight = FontWeight.Normal
    ) {
        EditSchoolFieldSurface(
            isError = isError,
                readOnly = readOnly
            ) {
                BasicTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.fillMaxWidth(),
                enabled = !readOnly,
                readOnly = readOnly,
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium.copy(
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Normal,
                    color = EditSchoolNavy
                ),
                cursorBrush = SolidColor(EditSchoolPrimary),
                keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
                decorationBox = { innerTextField ->
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.CenterStart
                    ) {
                        if (value.isBlank() && placeholder.isNotBlank()) {
                            Text(
                                text = placeholder,
                                style = MaterialTheme.typography.bodyMedium,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Normal,
                                color = EditSchoolMuted
                            )
                        }
                        innerTextField()
                    }
                }
            )
        }
    }
}

@Composable
internal fun EditSchoolDropdownField(
    label: String,
    value: String,
    options: List<EditSchoolOption>,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    required: Boolean = false,
    isError: Boolean = false,
    errorText: String = "",
    labelResolver: (String) -> String = { it }
) {
    var expanded by remember { mutableStateOf(false) }
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val anchorWidth = with(LocalDensity.current) { anchorSize.width.toDp() }
    val displayValue = labelResolver(value)

    AppLabeledField(
        label = label,
        required = required,
        errorText = errorText,
        modifier = modifier,
        verticalSpacing = 6.dp,
        labelStyle = MaterialTheme.typography.labelMedium,
        labelColor = EditSchoolNavy,
        requiredColor = EditSchoolDanger,
        labelFontWeight = FontWeight.SemiBold,
        errorStyle = MaterialTheme.typography.bodySmall,
        errorColor = EditSchoolDanger,
        errorFontWeight = FontWeight.Normal
    ) {
        // Keep the popup in the same layout box as the control. A sibling popup
        // of AppLabeledField can use the label column as its anchor, which makes
        // it jump when the surrounding form is remeasured or scrolled.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .onGloballyPositioned { anchorSize = it.size }
        ) {
            EditSchoolFieldSurface(
                isError = isError,
                readOnly = true,
                containerColor = EditSchoolCard,
                onClick = { expanded = !expanded }
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = displayValue,
                        style = MaterialTheme.typography.bodyMedium,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Normal,
                        color = if (displayValue.isBlank()) EditSchoolMuted else EditSchoolNavy,
                        modifier = Modifier.weight(1f)
                    )
                    Icon(
                        painter = painterResource(Res.drawable.chevron_down),
                        contentDescription = "Open dropdown",
                        tint = if (expanded) EditSchoolPrimary else EditSchoolNavy,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }

            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                containerColor = EditSchoolCard,
                shape = RoundedCornerShape(8.dp),
                shadowElevation = 10.dp,
                border = BorderStroke(1.dp, EditSchoolBorder),
                width = anchorWidth.takeIf { it > 0.dp }
            ) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = {
                            Text(
                                text = option.label,
                                color = EditSchoolNavy,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Medium
                            )
                        },
                        onClick = {
                            onValueChange(option.code)
                            expanded = false
                        }
                    )
                }
            }
        }
    }
}

@Composable
internal fun EditSchoolFieldSurface(
    isError: Boolean,
    readOnly: Boolean,
    containerColor: Color = if (readOnly) EditSchoolReadOnlyBackground else EditSchoolCard,
    height: Dp = 40.dp,
    onClick: (() -> Unit)? = null,
    content: @Composable RowScope.() -> Unit
) {
    val borderColor = when {
        isError -> EditSchoolDanger
        else -> EditSchoolInputBorder
    }
    val interactionSource = remember { MutableInteractionSource() }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .background(containerColor, EditSchoolFieldShape)
            .border(BorderStroke(1.dp, borderColor), EditSchoolFieldShape)
            .then(
                if (onClick != null) {
                    Modifier.clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        onClick = onClick
                    )
                } else {
                    Modifier
                }
            )
            .padding(horizontal = 12.dp, vertical = 5.dp),
        contentAlignment = Alignment.CenterStart
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            content = content
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun EditSchoolActionRow(
    modifier: Modifier = Modifier,
    isDirty: Boolean,
    isSaving: Boolean,
    onCancel: () -> Unit,
    onSave: () -> Unit
) {
    val saveClick: () -> Unit = {
        if (isDirty && !isSaving) {
            onSave()
        }
    }

    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Surface(
                modifier = Modifier
                    .width(116.dp)
                    .height(34.dp),
                shape = EditSchoolFieldShape,
                color = EditSchoolCard,
                border = BorderStroke(1.dp, EditSchoolBorder),
                onClick = onCancel
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        text = "Cancel",
                        style = MaterialTheme.typography.labelLarge,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = EditSchoolNavy
                    )
                }
            }
            Surface(
                modifier = Modifier
                    .width(128.dp)
                    .height(34.dp),
                shape = EditSchoolFieldShape,
                color = if (isDirty && !isSaving) EditSchoolPrimary else EditSchoolPrimary.copy(alpha = 0.55f),
                border = BorderStroke(1.dp, if (isDirty && !isSaving) EditSchoolPrimary else EditSchoolPrimary.copy(alpha = 0.55f)),
                onClick = saveClick
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        text = if (isSaving) "Saving..." else "Save",
                        style = MaterialTheme.typography.labelLarge,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Color.White
                    )
                }
            }
        }
    }
}

@Composable
internal fun EditSchoolOverviewValue(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            fontSize = 14.sp,
            fontWeight = FontWeight.ExtraBold,
            color = EditSchoolNavy
        )
        if (isLoading) {
            val transition = rememberInfiniteTransition(label = "edit_school_audit_shimmer")
            val shimmerX by transition.animateFloat(
                initialValue = -120f,
                targetValue = 240f,
                animationSpec = infiniteRepeatable(tween(900), RepeatMode.Restart),
                label = "edit_school_audit_shimmer_x"
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(18.dp)
                    .clip(RoundedCornerShape(5.dp))
                    .background(
                        Brush.linearGradient(
                            colors = listOf(Color(0xFFE8EDF6), Color(0xFFF8FAFD), Color(0xFFE8EDF6)),
                            start = Offset(shimmerX, 0f),
                            end = Offset(shimmerX + 120f, 0f)
                        )
                    )
            )
        } else {
            Text(
                text = value.ifBlank { "—" },
                style = MaterialTheme.typography.bodyMedium,
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                color = EditSchoolNavy
            )
        }
    }
}

@Composable
internal fun EditSchoolOverviewStatus(
    value: String,
    onValueChange: (String) -> Unit,
    compact: Boolean
) {
    EditSchoolDropdownField(
        label = "Status",
        value = value,
        options = editSchoolStatusOptions,
        onValueChange = onValueChange,
        required = true,
        labelResolver = { editSchoolStatusLabel(it) },
        modifier = Modifier.fillMaxWidth()
    )
}
